<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onMounted, ref } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore } from 'dashboard/composables/store';
import WootConfirmDeleteModal from 'dashboard/components/widgets/modal/ConfirmDeleteModal.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const store = useStore();
const router = useRouter();
const account = useAccount();

const isRefreshing = ref(false);
const isSubmitting = ref(false);
const showDeletePopup = ref(false);

const currentAccount = computed(() => account.currentAccount.value || {});
const customAttributes = computed(
  () => currentAccount.value.custom_attributes || {}
);
const isMarkedForDeletion = computed(
  () => !!customAttributes.value.marked_for_deletion_at
);
const deletionDate = computed(
  () => customAttributes.value.marked_for_deletion_at
);

const confirmPlaceHolderText = computed(
  () =>
    `Digite ${currentAccount.value.name || 'o nome da conta'} para confirmar`
);

const formattedDeletionDate = computed(() => {
  if (!deletionDate.value) {
    return '';
  }

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'full',
    timeStyle: 'short',
  }).format(new Date(deletionDate.value));
});

const successRoute = computed(() =>
  account.accountScopedRoute('deletar_conta_sucesso_index')
);

const syncAccount = async () => {
  isRefreshing.value = true;
  try {
    await store.dispatch('accounts/get', { silent: true });
  } finally {
    isRefreshing.value = false;
  }
};

const scheduleDeletion = async () => {
  showDeletePopup.value = false;
  isSubmitting.value = true;

  try {
    await store.dispatch('accounts/toggleDeletion', {
      action_type: 'delete',
    });
    await syncAccount();
    useAlert('A exclusão da conta foi agendada com sucesso.');
    router.push(successRoute.value);
  } catch {
    useAlert('Não foi possível agendar a exclusão da conta agora.');
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  syncAccount();
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section class="flex h-full flex-col overflow-hidden bg-[#fcfaf6]">
    <LaisHeader
      title="Deletar conta"
      helper="Agende a exclusão definitiva da conta Imudei e revise os impactos antes de confirmar."
    />

    <div class="flex-1 overflow-auto px-4 py-4 sm:px-6 lg:px-10 lg:py-6">
      <div
        class="grid gap-5 xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]"
      >
        <div class="grid gap-5">
          <LaisCard title="Antes de continuar">
            <div class="space-y-4 text-[13px] leading-6 text-lais-text-muted">
              <p>
                A exclusão agenda a remoção permanente da conta
                <strong class="text-lais-text">{{
                  currentAccount.name || 'Imudei'
                }}</strong>
                e dos dados operacionais vinculados a ela. Durante o período de
                carência, a conta ainda fica acessível para cancelamento.
              </p>

              <ul class="space-y-2 rounded-lais bg-white/80 px-4 py-4">
                <li>
                  Conversas, contatos, relatórios e ajustes serão removidos.
                </li>
                <li>
                  Integrações e automações deixam de ter continuidade após a
                  exclusão final.
                </li>
                <li>
                  Se houver outro administrador responsável, alinhe a decisão
                  antes de confirmar.
                </li>
              </ul>

              <div
                v-if="isMarkedForDeletion"
                class="rounded-lais border border-[#f1c9cf] bg-[#fff5f6] px-4 py-3 text-[#912018]"
              >
                <p class="font-semibold">Exclusão já agendada</p>
                <p class="mt-1">
                  A conta está marcada para exclusão em
                  <strong>{{ formattedDeletionDate }}</strong>
                  .
                </p>
              </div>

              <div class="flex flex-wrap gap-2">
                <button
                  v-if="!isMarkedForDeletion"
                  type="button"
                  class="inline-flex items-center gap-2 rounded-lais bg-[#b42318] px-4 py-2.5 text-[14px] font-semibold text-white transition-all hover:bg-[#912018] disabled:cursor-not-allowed disabled:opacity-60"
                  :disabled="isSubmitting || isRefreshing"
                  @click="showDeletePopup = true"
                >
                  <span class="i-lucide-trash-2 size-4" />
                  Solicitar exclusão da conta
                </button>

                <RouterLink
                  v-else
                  :to="successRoute"
                  class="inline-flex items-center gap-2 rounded-lais border border-lais-border bg-white px-4 py-2.5 text-[14px] font-semibold text-lais-text transition-all hover:border-lais-primary hover:text-lais-link"
                >
                  <span class="i-lucide-shield-alert size-4" />
                  Ver exclusão agendada
                </RouterLink>

                <RouterLink
                  :to="account.accountScopedRoute('meus_dados_index')"
                  class="inline-flex items-center gap-2 rounded-lais border border-lais-border bg-white px-4 py-2.5 text-[14px] font-semibold text-lais-text transition-all hover:border-lais-primary hover:text-lais-link"
                >
                  <span class="i-lucide-arrow-left size-4" />
                  Voltar para meus dados
                </RouterLink>
              </div>
            </div>
          </LaisCard>

          <LaisCard title="Checklist recomendado">
            <ol class="space-y-3 text-[13px] leading-6 text-lais-text-muted">
              <li>
                1. Revise se existe alguma exportação, relatório ou integração
                que precise ser preservada.
              </li>
              <li>
                2. Avise o time que usa esta conta para evitar perda inesperada
                de contexto operacional.
              </li>
              <li>
                3. Confirme a exclusão digitando o nome exato da conta no passo
                seguinte.
              </li>
            </ol>
          </LaisCard>
        </div>

        <div class="grid gap-5">
          <LaisCard title="O que acontece depois">
            <div class="space-y-3 text-[13px] leading-6 text-lais-text-muted">
              <p>
                A Imudei agenda a exclusão e mantém uma janela de reversão antes
                da remoção definitiva.
              </p>
              <p>
                Enquanto o pedido estiver ativo, você consegue acompanhar o
                status na tela de sucesso e cancelar a solicitação a qualquer
                momento.
              </p>
              <div class="flex flex-wrap gap-2">
                <LaisPrimaryButton
                  v-if="isMarkedForDeletion"
                  icon="i-lucide-arrow-right"
                  @click="router.push(successRoute)"
                >
                  Abrir status da exclusão
                </LaisPrimaryButton>
                <RouterLink
                  :to="account.accountScopedRoute('privacidade_index')"
                  class="inline-flex items-center gap-2 rounded-lais border border-lais-border bg-white px-3 py-2 font-semibold text-lais-text transition-all hover:border-lais-primary hover:text-lais-link"
                >
                  <span class="i-lucide-shield-half size-4" />
                  Abrir privacidade
                </RouterLink>
              </div>
            </div>
          </LaisCard>
        </div>
      </div>
    </div>
  </section>

  <WootConfirmDeleteModal
    v-if="showDeletePopup"
    v-model:show="showDeletePopup"
    title="Confirmar exclusão da conta"
    message="Essa ação agenda a exclusão permanente da conta e dos dados relacionados. Digite o nome da conta para continuar."
    confirm-text="Agendar exclusão"
    reject-text="Cancelar"
    :confirm-value="currentAccount.name || ''"
    :confirm-place-holder-text="confirmPlaceHolderText"
    @on-confirm="scheduleDeletion"
    @on-close="showDeletePopup = false"
  />
</template>
